
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/utils/production-logger';
import { UnifiedErrorScreen } from '@/components/error/UnifiedErrorScreen';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Error boundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      component: 'ErrorBoundary'
    });

    this.setState({
      error,
      errorInfo
    });

    this.props.onError?.(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    
    logger.info('Error boundary reset', {
      component: 'ErrorBoundary'
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <UnifiedErrorScreen
          variant="component"
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleReset}
          showHomeButton={true}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
